import { useEffect, useMemo, useState } from "react";
import Aside from "../../components/aside/aside.component";
import Footer from "../../components/footer/footer.component";
import Header from "../../components/header/header.component";
import FormModal from "./form.modal";
import { PartnerAccountsService } from "../../services/operation/partner-accounts";
import { UtilsService } from "../../services/utils";
import AlertPeriod from "../../components/alert-period/alert-period";
import usePeriod from "../../hooks/period.use";
import DatepickerComponent from "../../components/datepicker/datepicker.component";
import Button from "../../components/button/button.component";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import InvalidFeedback from "../../components/invalid-feedback/invalid-feedback.component";
import SelectComponent from "../../components/select/select.component";
import { PartnersService } from "../../services/partners";
import GeneralTable from "./general-table";
import EmptyState from "../../components/empty-state/empty-state.component";
import MonthTable from "./month-table";
import { ExcelService } from "../../utils/excel";

function PartnerAccountsPage() {
  const { currentPeriod } = usePeriod();

  const defaultValues = {
    range: "",
    partner: "-1",
  };

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<typeof defaultValues>({
    mode: "onChange",
    defaultValues,
  });

  const [showModal, setShowModal] = useState({
    data: null,
    show: false,
  });
  const [partners, setPartners] = useState<any[]>([]);
  const [type, setType] = useState("all");
  const [list, setList] = useState<any[]>([]);
  const [originalList, setOriginalList] = useState<any[]>([]);

  const totalIngresos = useMemo(() => {
    return list
      .filter((l) => l.type === "ingreso")
      .reduce((a, b) => a + b.amount, 0);
  }, [list]);

  const totalEgresos = useMemo(() => {
    return list
      .filter((l) => l.type === "egreso")
      .reduce((a, b) => a + b.amount, 0);
  }, [list]);

  const handleModal = (refresh?: boolean) => {
    if (refresh) {
      getList();
    }
    setShowModal({ data: null, show: false });
  };

  const getList = async () => {
    const data = await PartnerAccountsService.list(currentPeriod._id);
    setList(data);
    setOriginalList(data);
  };

  const getPartners = async () => {
    const data = await PartnersService.list();
    const all = { value: -1, text: "Todos los socios y colaboradores" };
    const f = data.map((row: any) => ({ value: row._id, text: row.name }));
    setPartners([all, ...f]);
  };

  const filter = (data: typeof defaultValues) => {
    const range = data.range;
    const partner = data.partner;

    if (!range && partner === "-1") {
      return setList(originalList);
    }

    const listFiltered = originalList
      .filter((row) => {
        return partner === "-1" || row.partner._id === partner;
      })
      .filter((row) => {
        return (
          range.length !== 2 ||
          moment(row.date).isBetween(
            moment(range[0]),
            moment(range[1]),
            "days",
            "[]"
          )
        );
      });

    setList(listFiltered);
  };

  const download = () => {
    ExcelService.fromTable(type === 'all' ? 'partner-accounts-general': 'partner-accounts-month');
  };

  useEffect(() => {
    getPartners();
    getList();
  }, []);

  return (
    <>
      <div className="d-flex flex-column flex-root">
        <div className="page d-flex flex-row flex-column-fluid">
          <Aside />
          <div
            className="wrapper d-flex flex-column flex-row-fluid"
            id="kt_wrapper"
          >
            <Header />
            <div
              className="content fs-6 d-flex flex-column flex-column-fluid"
              id="kt_content"
            >
              <div className="toolbar" id="kt_toolbar">
                <div className="container-fluid d-flex flex-stack flex-wrap flex-sm-nowrap">
                  <div className="d-flex flex-column align-items-start justify-content-center flex-wrap me-2">
                    <h1 className="text-gray-900 fw-bold my-1 fs-2">
                      Cuentas de socios y colaboradores
                      <small className="text-muted fs-6 fw-normal ms-1" />
                    </h1>
                    <ul className="breadcrumb fw-semibold fs-base my-1">
                      <li className="breadcrumb-item text-muted">Operación</li>
                      <li className="breadcrumb-item text-gray-900">
                        Cuentas de socios y colaboradores
                      </li>
                    </ul>
                  </div>
                  <div className="d-flex align-items-center flex-nowrap text-nowrap py-1">
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowModal({ data: null, show: true })}
                    >
                      <i className="ki-duotone ki-plus"></i> Crear cuenta
                    </button>
                  </div>
                </div>
              </div>

              <div className="container-xxl mb-5">
                <AlertPeriod />
              </div>

              <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
                <div className="container-xxl">
                  <div className="card mb-4">
                    <div className="card-body">
                      <h4 className="mb-6">
                        Filtros <i className="bi bi-funnel-fill" />
                      </h4>

                      <div className="d-flex align-items-end column-gap-4">
                        <div className="flex-grow-1">
                          <Controller
                            name="range"
                            control={control}
                            rules={{ required: false }}
                            render={({
                              field: { onChange, value },
                              fieldState: { error },
                            }) => {
                              let valueFormatted = "";

                              if (Array.isArray(value) && value.length === 2) {
                                const start = moment(value[0]).format(
                                  "DD-MM-YYYY"
                                );
                                const end = value[1]
                                  ? moment(value[1]).format("DD-MM-YYYY")
                                  : "";
                                valueFormatted = `Del ${start} al ${end}`;
                              }
                              return (
                                <>
                                  <DatepickerComponent
                                    label="Rango de fechas"
                                    errors={error}
                                    onChange={onChange}
                                    value={valueFormatted}
                                    mode="range"
                                    disable={[
                                      {
                                        from: moment()
                                          .add(1, "days")
                                          .format("YYYY-MM-DD"),
                                        to: moment()
                                          .add(1, "years")
                                          .format("YYYY-MM-DD"),
                                      },
                                    ]}
                                  />
                                  <InvalidFeedback error={error} />
                                </>
                              );
                            }}
                          />
                        </div>
                        <div className="flex-grow-1">
                          <SelectComponent
                            label="Socio y colaborador"
                            data={partners}
                            {...register("partner", { required: false })}
                            errors={errors.partner}
                          />
                        </div>
                        <div className="">
                          <Button
                            className="btn btn-primary"
                            onClick={handleSubmit(filter)}
                          >
                            Filtrar
                          </Button>
                        </div>
                        <div className="nowrap">
                          <Button className="btn btn-light-primary" onClick={download}>
                            <i className="bi bi-download"></i>
                            Descargar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 d-flex column-gap-4">
                    <div className="card flex-grow-1 text-center">
                      <div className="card-body">
                        <div className="fs-2x fw-bold">
                          $ {UtilsService.numberFormat(totalIngresos)}
                        </div>
                        <div className="fs-4 fw-semibold text-gray-500">
                          Ingresos
                        </div>
                      </div>
                    </div>
                    <div className="card flex-grow-1 text-center">
                      <div className="card-body">
                        <div className="fs-2x fw-bold">
                          $ {UtilsService.numberFormat(totalEgresos)}
                        </div>
                        <div className="fs-4 fw-semibold text-gray-500">
                          Egresos
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-body py-4">
                      <div
                        className="dt-container dt-bootstrap5 dt-empty-footer"
                        id="kt_table_users_wrapper"
                      >
                        <ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bold mb-6">
                          <li className="nav-item mt-2">
                            <button
                              className={`nav-link text-active-primary ms-0 me-10 py-5 ${
                                type === "all" ? "active" : ""
                              }`}
                              onClick={() => setType("all")}
                            >
                              General
                            </button>
                          </li>
                          <li className="nav-item mt-2">
                            <button
                              className={`nav-link text-active-primary ms-0 me-10 py-5 ${
                                type === "month" ? "active" : ""
                              }`}
                              onClick={() => setType("month")}
                            >
                              Acumulado por mes
                            </button>
                          </li>
                        </ul>

                        <div className="table-responsive" id="">
                          {type === "all" && <GeneralTable list={list} setShowModal={setShowModal} />}
                          {type === "month" && <MonthTable list={list} />}

                          {list.length === 0 && <EmptyState />}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <FormModal
        show={showModal.show}
        data={showModal.data}
        setShow={handleModal}
      />
    </>
  );
}

export default PartnerAccountsPage;
